import { EBookingPaymentStatus } from '@app/@shared/payment-status/payment-status.types';
import { EBookingSearchStatus, EBookingStages } from './statuses';

export interface BookingContext {
  statusGroupId?: EBookingSearchStatus;
  url?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface SecurityCheckData {
  airlineId?: number;
  onProceed: (data: SecurityCheckAnswer[]) => void;
  onDiscard: () => void;
}

export declare interface BookingCreation {
  isValid(markAsDirty?: boolean): boolean;
  getResult(): unknown;
}

export const BookingTypes = {
  1: 'Door To Door',
  2: 'Door To Airport',
  3: 'Airport To Door',
};

export enum EBookingType {
  DoorToDoor = 1,
  DoorToAirport = 2,
  AirportToDoor = 3,
}

export const PodTypes = {
  1: 'Customer Signature',
  2: 'Pictures',
  3: 'EID Picture (Front and Back)',
  4: 'Passport',
  5: 'Personal ID',
  6: 'EID - By card reader',
  7: 'Booking QR code',
};

export const PaymentMethodTypes = {
  1: 'Debit/Credit Card',
  2: 'Use My Credits',
};

export interface Driver {
  id: number;
  isMaskedCall: true;
  name: string;
  phone: string;
  liveTrackingEnabled: boolean;
  eta: number;
  etaDescription: string;
  latitude: number;
  longitude: number;
  profileImageUrl: string;
  taskTypeId: number;
  taskTypeTitle: string;
  isPickup: boolean;
  isDropoff: boolean;
}

export interface TrackerStage {
  stage: EBookingStages;
  title: string;
  description: string;
  progress: 2;
  stageType: string;
  startTime: string;
  endTime: string;
}

export interface ProgressTracker {
  stageTitle: string;
  stageDescription: string;
  showEstimatedTime: boolean;
  estimatedDeliveryTime: number;
  stages: TrackerStage[];
}

export enum EBookingStatusStyleGroup {
  Active = 1,
  Completed = 2,
  Failed = 3,
  FailedDelivery = 4,
}

export interface Booking {
  id: number;
  organization: string;
  airport: Airport;
  bookingTypeId: keyof typeof BookingTypes;
  bagsQuantities: BagsQuantity[];
  pickupAddress: Address;
  destinationAddress: Address;
  bags: Bag[];
  flight: Flight;
  bountyInfo: Bounty;
  service: Service;
  events: Event[];
  pickupWindow: TimeWindow;
  dropOffWindow: TimeWindow;
  trackingUrl: string;
  statusId: number;
  statusTitle: string;
  /** Payment state, separate from the booking status/stage. */
  paymentStatusId?: EBookingPaymentStatus;
  /** Localized by the backend. */
  paymentStatusTitle?: string;
  stageTitle?: string;
  styleGroup: EBookingStatusStyleGroup;
  canCancel: boolean;
  canShowQR: boolean;
  canChangeService: boolean;
  canPay: boolean;
  dropoffEditable: boolean;
  pickupEditable: boolean;
  progress: ProgressTracker;
  ticketNumberOrPnr: string;
  totalPassengers?: number;
  price: number;
  currency: string;
  paymentLink: string;
  lastRefundOperation: lastRefundOperation[];
  verificationQrCode: string;
  proofOfDelivery: Pods[];
  transactions: transactions[];
  paymentMethods: paymentMethod[];
  driver: Driver;
  canAddExtraDetails: boolean;
  isCompleted: boolean;
  bookingType: {
    description: string;
    iconUrl: string;
    id: number;
    name: string;
  };
  invoices: Invoice[];
  travelClassType: {
    id: number;
    name: string;
  };
  priceBreakdownSummary: {
    basePrice: number;
    vat: number;
    totalPrice: number;
    discount?: number;
  };
  payment?: {
    initialPaymentMethod?: number;
  };
  slotReservationExpiresAt?: number;
  isSlotReservationExpired?: boolean;
  slotHoldType: number;
  subscriptions?: string[];
}

export type SlotUiState =
  | 'HOLD_ACTIVE'
  | 'POST_RESELECT_HOLD'
  | 'EXPIRED_SLOT_AVAILABLE'
  | 'EXPIRED_SLOT_UNAVAILABLE'
  | 'NO_PICKUP_SLOTS'
  | 'NO_DROPOFF_SLOTS'
  | 'NO_SLOTS'
  | 'D2D_PICKUP_PASSED'
  | 'D2A_RELEVANT_PASSED'
  | 'A2D_RELEVANT_PASSED'
  | 'ALL_PASSED'
  | 'NO_BAGS'
  | null;

export interface Invoice {
  amount: number;
  currency: string;
  data: {
    invoicePdfLink?: string;
    cardHolderNmae?: string;
    cardNumber?: string;
  };
  id: number;
  message: null;
  paymentDate: number;
  paymentGatewayType: number;
  referenceKey: string;
  status: string;
  statusId: number;
  breakdown?: {
    basePrice: number;
    totalPrice: number;
    vat: number;
  };
}

export interface BookingInfo {
  pickupAddressId: number;
  destinationAddressId: number;
  flightCode: string;
  flightDepartureDate: Date;
}

export interface BagsPricing {
  priceBreakDown: priceBreakDown[];
  totalPrice: {
    amount: number;
    currency: string;
  };
}

export interface TimeWindow {
  endTime: number;
  startTime: number;
  endTimeOffset?: string;
  startTimeOffset?: string;
  storageFees: Fee;
  isPreviousBookingSlot?: boolean;
  hasCapacity?: boolean;
  isLimitedAvailability?: boolean;
  totalCapacity?: number;
  usedCapacity?: number;
}

/** Edit-slot windows enveloped with their real-time slot availability groups. */
export interface SlotWindowsResponse {
  data: TimeWindow[];
  subscriptions?: string[];
}

export interface Fee {
  amount: number;
  currency: string;
}

export interface Bounty {
  name: string;
  profileImageUrl: string;
}

export interface Event {
  description: string;
  timestamp: number;
  eventTypeId?: number;
}
export interface Service {
  name: string;
  description: string;
}

export interface BookingAvailability {
  bookingBlockedStartDate: number | null;
  bookingBlockedEndDate: number | null;
}

export function isBookingCurrentlyPaused(
  availability: BookingAvailability | null,
  atTime: number = Date.now(),
): boolean {
  if (!availability?.bookingBlockedStartDate) {
    return false;
  }
  const { bookingBlockedStartDate, bookingBlockedEndDate } = availability;
  return (
    atTime >= bookingBlockedStartDate &&
    (bookingBlockedEndDate == null || atTime <= bookingBlockedEndDate)
  );
}

export function isDateBookingBlocked(
  date: Date | null,
  availability: BookingAvailability | null,
): boolean {
  if (!date || !availability?.bookingBlockedStartDate) {
    return false;
  }
  // bookingBlockedStartDate/EndDate are UTC-midnight day boundaries, not instants.
  // Compare using the calendar date's own Y/M/D as UTC midnight so the result
  // doesn't depend on the viewer's timezone offset.
  const utcMidnight = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  return isBookingCurrentlyPaused(availability, utcMidnight);
}

export interface Tracking {
  link: string;
  history: History[];
}

export interface Flight {
  code: string;
  departureAirport: FlightAirport;
  arrivalAirport: FlightAirport;
  plannedDepartureTime: number;
  plannedArrivalTime: number;
  plannedDepartureTimeOffset?: string;
  plannedArrivalTimeOffset?: string;
  airlineIconUrl: string;
  /** Passenger-flow branding logo; null when the airline has none configured. */
  airlineLogoUrl?: string | null;
  airlineName: string;
  airlineId: number;
}

export interface FlightAirport {
  code: string;
  name: string;
  timezone: string;
}

export interface History {
  date: number;
  description: string;
}
export enum EBagStatusStyleGroup {
  Pending = 1,
  Active = 2,
  Delivered = 3,
  Failed = 4,
}
export interface Bag {
  id: number;
  barcode: string;
  imageUrls: string[];
  pictureUrls: string[];
  styleGroup: EBagStatusStyleGroup;
  status: number;
  statusTitle: string;
}

export interface Airport {
  id: number;
  name: string;
}

export interface BagsQuantity {
  bagType: bagType[];
  bagTypeId: number;
  quantity: number;
}

export interface bagType {
  imageUrl: string;
  name: string;
}

export interface Customer {
  name: string;
  phone: string;
  pickupAddress: Address;
}

export interface Address {
  id: number;
  name: string;
  city: string;
  phone: string;
  latitude: number;
  longitude: number;
  addressType: number;
  area: string;
  building: string;
  floor: string;
  unit: string;
  note: string;
  distanceKm?: number;
  zone?: number;
}

export interface SecurityCheckQuestions {
  hazardousItemsImage: string;
  questions: Question[];
}

export interface Question {
  id: number;
  content: string;
  answers: Answer[];
  message: string;
  validityTypeId: number;
}

export interface Answer {
  id: number;
  content: string;
  message: string;
  validityTypeId: number;
  isSelected: boolean;
}

export interface bagSpecs {
  compliancePolicyUrl: string;
  isBagImageRequired: boolean;
  bookingTypeId: number;
  pricingDescription: string;
  specs: spec[];
}
export interface spec {
  bagTypeId: number;
  name: string;
  maxWeightInKg: number;
  maxHeightInCm: number;
  maxWidthInCm: number;
  maxLengthInCm: number;
  isAvailable: boolean;
  unitPrice: number;
  currency: string;
  imageUrl: string;
  maxNumberOfBags: number;
  quantity: number;
  totalPrice: number;
  bagInfo?: DraftBag[];
}

export interface lastRefundOperation {
  id: number;
  timestamp: number;
  statusId: number;
  description: string;
}

export const RefundTypes = {
  1: {
    name: 'Refund In Progress',
    style: 'refund-pending',
  },
  2: {
    name: 'Refund Succeeded',
    style: 'refund-success',
  },
  3: {
    name: 'Refund Failed',
    style: 'refund-failed',
  },
};

export interface MaskedCallFailurePayload {
  phoneNumber: string;
}

export interface Pods {
  proofImages: string;
  proofTypeId: keyof typeof PodTypes;
  title: string;
}

export interface Issue {
  id: number;
  text: string;
  requireCustomReason: boolean;
}

export interface transactions {
  description: string;
  id: number;
  statusId: number;
  timeStamp: number;
  title: string;
  type: number;
}

export const PaymentTypes = {
  1: {
    name: 'Pending',
    style: 'pending',
  },
  2: {
    name: 'Processing',
    style: 'processing',
  },
  3: {
    name: 'Paid',
    style: 'paid',
  },
  4: {
    name: 'Refunded',
    style: 'refunded',
  },
};

export interface preBookingPricing {
  priceBreakDown: priceBreakDown[];
  paymentMethods: paymentMethod[];
}

export interface priceBreakDown {
  amount: number;
  category: string;
  currency: string;
  typeId: number;
}

export interface paymentMethod {
  id: number;
  name?: string;
  description?: string;
  enabled?: boolean;
  disableReason?: string;
  amount?: number;
  hint?: string;
}

export interface refundInfo {
  amount: number;
  currency: string;
  options: options[];
}

export interface options {
  id: number;
  name: string;
  hint: string;
  enabled: boolean;
  disableReason: string;
}

export interface CancelBookingOption {
  id: number;
  title: string;
  hint?: string;
  isSelected?: boolean;
  refundAmount?: number;
  currency?: string;
  refundMethodOptions?: options[];
}

export interface ChangeServiceOption {
  bookingTypeId: number;
  title: string;
  hint?: string;
  amountMessage?: string;
  isAvailable: boolean;
  isSelected?: boolean;
}

export interface ChangeServicePricing {
  serviceChangeId: number;
  pricingBreakDown: priceBreakDown[];
  paymentMethods: paymentMethod[];
  refundInfo: refundInfo;
  canPay: boolean;
}

export interface ChangeServiceConfirmResult {
  paymentId?: number;
  paymentLink?: string;
  price?: number;
  currency?: string;
}

export interface TimeSlotsRequest {
  bookingTypeId: number;
  pickupAddressId?: number;
  destinationAddressId?: number;
  flightCode?: string;
  flightDepartureDate?: number;
  // The airline-booking flow's createPreBooking() doesn't send bag counts.
  totalBags?: number;
  oversizeBags?: number;
}

export interface TimeSlotsResponse {
  pickupWindows?: TimeWindow[];
  dropoffWindows?: TimeWindow[];
  existingPickupWindow?: TimeWindow;
  existingDropoffWindow?: TimeWindow;
  existingBookingId?: number;
  /** Reason shown in the empty state when no pickup slots are available (e.g. daily quota reached). */
  pickupUnavailableMessage?: string;
  /** Reason shown in the empty state when no drop-off slots are available (e.g. daily quota reached). */
  dropoffUnavailableMessage?: string;
  /** Live-update groups (e.g. `slotavailability:pickup:20261001`) to watch for capacity changes. */
  subscriptions?: string[];
}

export interface BagsSpecsRequest {
  destinationAddressId?: number;
  flightCode?: string;
  flightDepartureDate?: number;
}

/**
 * A bag added by the customer during the "bags information" wizard step,
 * before it has been persisted to a booking. Distinct from `Bag` (the
 * persisted/API bag shape returned once a booking exists).
 */
export interface DraftBag {
  bagTypeId: number;
  barCode: string;
  ImageUrls: string;
  ImageUrl?: string;
  unitPrice?: number;
}

/** One row of the by-bag-type grouping shown in the bags-information step. */
export interface BagListItem {
  bagInfo: DraftBag[];
  bagTypeId: number;
  totalPrice: number;
  quantity: number;
}

/** Running selection used to price the bags step (per bag type or per draft bag). */
export interface SelectedBag {
  id: number;
  count: number;
  imageUrl?: string;
  bagInfo?: DraftBag[];
}

/** Payload shape pushed into `bagsQuantities` when bags are selected by quantity only. */
export interface SelectedBagQuantity {
  bagTypeId: number;
  quantity: number;
  imageUrl?: string;
}

/** Result returned by the bags-information step's `getResult()`. */
export interface BagsStepResult {
  bags?: DraftBag[];
  bagsQuantities?: SelectedBagQuantity[];
}

export interface BagRemoveEvent {
  bag: DraftBag;
  index: number;
}

export interface EditBagResult {
  typeId: number;
  barCode: string;
  ImageUrls: string;
}

export interface BagEditEvent extends EditBagResult {
  index: number;
}

/** Data passed into `AddBagDrawerComponent` whether opened as a drawer or a dialog. */
export interface AddBagDrawerData {
  drawerData: spec & { isBagImageRequired: boolean };
  onProceedFromAddBag: (bag: DraftBag) => void;
  isDoor2Airport: boolean;
  existingBarcodes: string[];
}

/**
 * An airport as returned by the flight-search endpoint (`BookingService.getFlights`).
 * Richer than the persisted-booking `FlightAirport` — includes the airport's own id
 * (used to add a pickup/dropoff address against that airport) plus geo/city data used
 * to drive the pickup/dropoff address step.
 */
export interface FlightSearchAirport extends FlightAirport {
  id: number;
  supportedCities: number[];
  latitude: number;
  longitude: number;
}

/** One selectable delivery-service option attached to a flight-search result. */
export interface FlightSearchService {
  id: number;
  name: string;
  description: string;
  isAvailable: boolean;
  reason?: string;
}

/**
 * A flight as returned by the flight-search endpoint (`BookingService.getFlights`),
 * used while picking a flight during booking creation. Distinct from the persisted
 * `Flight` on a `Booking`: search results carry richer airport data and the list of
 * delivery services available for that flight.
 */
export interface FlightSearchResult extends Omit<
  Flight,
  'departureAirport' | 'arrivalAirport'
> {
  departureAirport: FlightSearchAirport;
  arrivalAirport: FlightSearchAirport;
  services: FlightSearchService[];
}

/**
 * Merged result produced by `FlightInformationComponent.getResult()` once a flight has
 * been selected — consumed by the pickup/dropoff address steps (`LocationInformationComponent`,
 * `PassengerLocationInformationComponent`) to know the flight code and airport ids.
 */
export interface FlightInformationResult extends Partial<FlightSearchResult> {
  flightCode?: string;
  flightDepartureDate?: number;
  ticketNumberOrPnr?: string;
  travelClass?: number;
  totalPassengers?: number;
}

/** One answer submitted from the security-check dialog (`SecurityCheckDialogComponent`). */
export interface SecurityCheckAnswer {
  questionId: number;
  answerId: number;
}

/**
 * Response from `confirmPayment`/`confirm` (booking + airline-booking) — used by
 * `AddBagsDialogComponent` to decide whether extra payment is still required and,
 * if so, to render the pricing/payment-methods step.
 */
export interface ConfirmPaymentResult {
  id: number;
  canPay?: boolean;
  price?: number;
  currency?: string;
  paymentInformation?: {
    priceBreakDown?: priceBreakDown[];
    paymentMethods?: paymentMethod[];
  };
}

/** Response from `payBooking` (booking + airline-booking). */
export interface PayBookingResult {
  id: number;
  paymentId?: number;
  paymentLink?: string;
  price?: number;
  currency?: string;
}

/**
 * `MAT_DIALOG_DATA` shape for `AddBagsDialogComponent` (booking + airline-booking).
 * Always opened with a real `bagSpecs` result merged with the current `Booking`
 * (see `BookingDetailsBaseComponent.getBagsSpecs`), plus a few caller-supplied extras.
 */
export interface AddBagsDialogData extends bagSpecs {
  id?: number;
  serviceType?: number;
  isDoor2Airport?: boolean;
  bookingInfo?: BookingInfo;
  bags?: Bag[];
  pickupWindow?: TimeWindow;
  dropOffWindow?: TimeWindow;
  flight?: Flight;
  ticketNumberOrPnr?: string;
  pickupAddress?: Address;
  destinationAddress?: Address;
  onPayLater?: () => void;
}

/**
 * The verified-deeplink passenger record for airline bookings — the `verifyToken`
 * response, assigned to `CreateBookingComponent.passengerDetails` and passed down to
 * `PassengerInformationComponent` and `NumberConfirmationComponent`. Both `email` and
 * `passengerEmail` are read defensively since the exact field name isn't guaranteed.
 */
export interface AirlineBookingPassengerDetails {
  name?: string;
  email?: string;
  passengerEmail?: string;
  phone?: string;
  phoneNumber?: string;
  pnr?: string;
  class?: string;
  airlineName?: string;
  flights?: { flightCode?: string }[];
}

/**
 * `TimeSlotsResponse` as consumed by `DeliverySlotsComponent`/`PassengerDeliverySlotsComponent`:
 * adds the fallback timezone ids used to render a UTC-offset label before any window has been
 * selected (when there are no windows to read a `startTimeOffset` from yet).
 */
export interface DeliverySlotsData extends TimeSlotsResponse {
  pickupTimeZoneId?: string;
  dropoffTimeZoneId?: string;
}

export interface DeeplinkFlight {
  flightCode: string;
  flightDate: string | number;
}

/** Response of the airline-booking deeplink verify-token endpoint. */
export interface DeeplinkVerifyResponse extends AirlineBookingPassengerDetails {
  status?: { id: number };
  token: string;
  flights: DeeplinkFlight[];
  /** Configured airline branding logo; null when the airline has none. */
  airlineLogoUrl?: string | null;
}

/** Result of the door-to-door/door-to-airport/airport-to-door service selection step. */
export interface BookingTypeSelection {
  bookingTypeId: number;
  iconUrl?: string;
}

/** Result of the delivery-slots step: the user's chosen pickup/dropoff windows. */
export interface DeliverySlotsStepResult {
  pickupWindow?: TimeWindow;
  dropOffWindow?: TimeWindow;
}

export interface BagsEligibility {
  canManage: boolean;
  disabledReason?: string;
  // UTC ISO string; only set while canManage is true.
  editableUntil?: string;
}

export type ManageBagsStatus = 'Completed' | 'PaymentRequired' | 'NotPossible';

export interface ManageBagsRequest {
  currentBagCount: number;
  targetBagCount: number;
  // Only set for increases — mirrors the `paymentMethods: [{id, amount}]`
  // shape every other pay-and-confirm endpoint in this app expects
  // (see add-bags-dialog/change-service-dialog/sms-link payment-details).
  paymentMethods?: paymentMethod[];
}

export interface ManageBagsResponse {
  // Some backend environments serialize this enum as its numeric value
  // (0/1/2) instead of the documented string — see normalizeManageBagsStatus.
  status: ManageBagsStatus | number;
  originalBagCount: number;
  requestedBagCount: number;
  resultingBagCount?: number;
  reason?: string;
  paymentLink?: string;
  paymentCorrelationId?: string;
  amount?: number;
  currency?: string;
  expiresAt?: string;
}

const MANAGE_BAGS_STATUS_BY_INDEX: ManageBagsStatus[] = [
  'Completed',
  'PaymentRequired',
  'NotPossible',
];

export function normalizeManageBagsStatus(
  status: ManageBagsStatus | number,
): ManageBagsStatus {
  if (typeof status === 'number') {
    return MANAGE_BAGS_STATUS_BY_INDEX[status] ?? 'NotPossible';
  }
  return status;
}
